import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useSession } from "@/hooks/use-session";
import {
	useRequirements,
	useProgress,
	useUpdateRequirementsStatus,
	useDrives,
} from "@/hooks/use-ballot-access";
import { RequirementsCard } from "@/components/ballot-access/RequirementsCard";
import { ProgressTracker } from "@/components/ballot-access/ProgressTracker";
import { DriveList } from "@/components/ballot-access/DriveList";
import { DriveCreateModal } from "@/components/ballot-access/DriveCreateModal";
import { trackBallotAccessPetitionFiled } from "@/lib/analytics-events";

export const Route = createFileRoute("/_app/_/ballot-access/")({
	component: BallotAccessPage,
});

function BallotAccessPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const [createModalOpen, setCreateModalOpen] = useState(false);

	const { data: requirements, isLoading: reqLoading } = useRequirements(
		campaignId ?? 0,
	);
	const { data: progress, isLoading: progLoading } = useProgress(
		campaignId ?? 0,
	);
	const { data: drives = [], isLoading: drivesLoading } = useDrives(
		campaignId ?? 0,
	);
	const updateStatus = useUpdateRequirementsStatus(campaignId ?? 0);

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<p className="text-muted-foreground">Loading campaign...</p>
			</div>
		);
	}

	const showProgress =
		requirements?.status === "active" || requirements?.status === "met";

	function handleMarkFiled() {
		updateStatus.mutate(
			{ status: "filed" },
			{
				onSuccess: () => {
					trackBallotAccessPetitionFiled();
				},
			},
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-3xl space-y-6">
				<div>
					<h1 className="text-2xl font-semibold">Ballot Access</h1>
					<p className="text-sm text-muted-foreground">
						Track your signature collection requirements and petition drives.
					</p>
				</div>

				{reqLoading ? (
					<div className="h-48 rounded-lg border bg-card animate-pulse" />
				) : (
					<RequirementsCard
						campaignId={campaignId}
						requirements={requirements ?? null}
					/>
				)}

				{showProgress &&
					(progLoading ? (
						<div className="h-48 rounded-lg border bg-card animate-pulse" />
					) : progress ? (
						<ProgressTracker
							progress={progress}
							onMarkFiled={handleMarkFiled}
						/>
					) : null)}

				{showProgress &&
					(drivesLoading ? (
						<div className="h-32 rounded-lg border bg-card animate-pulse" />
					) : (
						<DriveList
							campaignId={campaignId}
							drives={drives}
							onCreateClick={() => setCreateModalOpen(true)}
						/>
					))}
			</div>

			<DriveCreateModal
				campaignId={campaignId}
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
			/>
		</div>
	);
}
